import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

/**
 * JugeBoxeQrManager
 *
 * Teacher-only component.
 * Select a class → generate printable QR codes for every student in the class.
 */
export default function JugeBoxeQrManager() {
  const navigate = useNavigate();

  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classesError, setClassesError] = useState('');

  const [classeId, setClasseId] = useState('');

  // Load classes on mount
  useEffect(() => {
    setLoadingClasses(true);
    api.get('/classes')
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setClasses(raw);
      })
      .catch(err => {
        setClassesError(err?.response?.data?.error || err?.message || 'Impossible de charger les classes.');
      })
      .finally(() => setLoadingClasses(false));
  }, []);

  const selectedClasse = classes.find(c => String(c._id) === classeId);

  const handlePrint = () => {
    if (!classeId) return;
    const nom = encodeURIComponent(selectedClasse?.nom || '');
    navigate(`/apps/juge-boxe/qr-print?classeId=${classeId}&nom=${nom}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#08080e',
        background: 'linear-gradient(120deg, #1c1c2b 50%, #a51c30 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 520 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/apps/juge-boxe')}
          sx={{ mb: 3, color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
        >
          ← Retour
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <QrCode2Icon sx={{ color: '#fff', fontSize: 32 }} />
          <Typography
            variant="h5"
            sx={{ color: '#fff', fontWeight: 700, fontFamily: 'Montserrat, sans-serif' }}
          >
            Générer QR codes élèves
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#bbb', mb: 3 }}>
          Sélectionnez une classe pour générer et imprimer les QR codes d'accès de tous les élèves.
        </Typography>

        <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.97)' }}>
          {/* Classes */}
          {loadingClasses ? (
            <CircularProgress size={24} />
          ) : classesError ? (
            <Alert severity="error">{classesError}</Alert>
          ) : (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Classe</InputLabel>
              <Select
                value={classeId}
                label="Classe"
                onChange={e => setClasseId(e.target.value)}
              >
                <MenuItem value=""><em>Choisir une classe…</em></MenuItem>
                {classes.map(c => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.nom} {c.niveau ? `(${c.niveau})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="contained"
            fullWidth
            startIcon={<PrintIcon />}
            disabled={!classeId}
            onClick={handlePrint}
            sx={{ bgcolor: '#a51c30', '&:hover': { bgcolor: '#8b1526' }, fontWeight: 700 }}
          >
            Générer et imprimer les QR codes
          </Button>

          <Typography variant="caption" display="block" sx={{ mt: 2, color: '#888', textAlign: 'center' }}>
            Un QR code unique est généré pour chaque élève. Vous pouvez régénérer à tout moment pour révoquer les anciens.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}

